<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"            
            :actions="actions"
            :refresh.sync="refreshTable"
            :ajax-params="aliasesParams"
            ajax-path="postfix/aliases"
            @ajaxResponse="(response) => {
                $store.commit('postfix/aliases/setItems', response);
            }"
        />
        <app-modal
            :show.sync="showEditModal"
            width="md:w-1/2 lg:w-1/3"
            @close="editModalClosed"
        >
            <template slot="header">Edit {{ editForm.address }}</template>
            <template slot="body">
                <div class="flex">
                    <div class="mb-4 mt-2">To</div>
                    <div class="ml-3 flex-1">
                        <div
                            v-for="(goto, index) in editForm.goto"
                            class="mb-3"
                        >               
                            <div class="flex items-center">
                                <input                        
                                    v-model="editForm.goto[index]"
                                    class="border px-3 py-2 w-full rounded"
                                    placeholder="Email" 
                                />
                                <button 
                                    class="p-2 rounded hover:bg-red-600 bg-red-500 text-white ml-2 shadow"
                                    v-if="editForm.goto.length > 1"
                                    @click="deleteGoto('edit', index)"
                                ><delete-icon width="20" height="20"/></button>
                                <button 
                                    v-if="index == (editForm.goto.length - 1)"
                                    class="p-2 rounded hover:bg-green-600 bg-green-500 text-white ml-2 shadow"
                                    @click="addGoto('edit')"
                                ><add-icon width="20" height="20"/></button>
                            </div>
                            <div 
                                class="text-red-500 mt-1" 
                                v-if="editForm.errors.has(`goto.${index}`)"
                            >{{ editForm.errors.get(`goto.${index}`) }}</div>
                        </div>
                    </div>
                </div>                           
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="md:w-1/2 lg:w-1/4"
            @close="addModalClosed"
        >
            <template slot="header">Add Alias</template>
            <template slot="body">                
                <app-select
                    v-model="addForm.domain"
                    placeholder="Domain"
                    :error="addForm.errors.get('domain')"
                    @updated="addForm.errors.clear('domain')"
                    required
                    focus
                    :options="items.domains"
                    value-key="domain"
                    label-key="domain"                  
                />
                <app-input
                    v-model="addForm.address"
                    placeholder="Alias"
                    :error="addForm.errors.get('address')"
                    @updated="addForm.errors.clear('address')"
                    help-text='To create a catch-all use an "*" as alias.'
                    required
                />
                <div class="flex mt-5">
                    <div class="mb-4 mt-2">To</div>
                    <div class="ml-3 flex-1">
                        <div
                            v-for="(goto, index) in addForm.goto"
                            class="mb-3"
                        >               
                            <div class="flex items-center">
                                <input                        
                                    v-model="addForm.goto[index]"
                                    class="border px-3 py-2 w-full rounded"
                                    placeholder="Email" 
                                />
                                <button 
                                    class="p-2 rounded hover:bg-red-600 bg-red-500 text-white ml-2 shadow"
                                    v-if="addForm.goto.length > 1"
                                    @click="deleteGoto('add', index)"
                                ><delete-icon width="20" height="20"/></button>
                                <button 
                                    v-if="index == (addForm.goto.length - 1)"
                                    class="p-2 rounded hover:bg-green-600 bg-green-500 text-white ml-2 shadow"
                                    @click="addGoto('add')"
                                ><add-icon width="20" height="20"/></button>
                            </div>
                            <div 
                                class="text-red-500 mt-1" 
                                v-if="addForm.errors.has(`goto.${index}`)"
                            >{{ addForm.errors.get(`goto.${index}`) }}</div>
                        </div>
                    </div>
                </div>             
                <div class="mt-8 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    import debounce from 'lodash/debounce';
    import StatusComponent from '@/components/Status.vue'

    export default {
        props: {
            domain: {
                type: String,
                default: null,
            }
        },
        components: {
            StatusComponent,
            AddIcon: () => import('@/icons/Add.vue'),
            DeleteIcon: () => import('@/icons/Delete.vue')                      
        },
        data() {
            return {
                addForm: new Form({
                    domain: null,
                    address: null,
                    goto: [''],
                }),
                editForm: new Form({
                    address: null,
                    goto: [],
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                tableIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                refreshTable: false,
                aliasesParams: {},
                columns: [
                    {
                        title: 'Alias',
                        field: 'address',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'To', 
                        field: 'goto', 
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Domain',
                        field: 'domain',
                        sorter: 'string',
                        minWidth: 200,
                        visible: !this.domain,
                    },
                    {
                        title: 'Last Modified',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 150,
                    },                    
                    {
                        title: 'Status', 
                        field: 'active', 
                        formatter({_cell}) {
                            if (_cell.value) {
                                return '<span class="px-3 py-1 rounded-full shadow bg-green-200 text-green-700">Active</span>';
                            }

                            return '<span class="px-3 py-1 rounded-full shadow bg-red-200 text-red-700">Inactive</span>';
                        },
                        minWidth: 130,
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: aliases => {
                            this.edit(aliases[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: aliases => {
                            this.activate(aliases[0]);
                        },
                        isEligible(aliases) {
                            return !aliases[0].active;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: aliases => {
                            this.deactivate(aliases[0]);
                        },
                        isEligible(aliases) {
                            return aliases[0].active;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: aliases => {
                            this.delete(aliases[0]);
                        },
                        requiredSelect: true,
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                items: 'postfix/aliases/items',          
            }),
        },
        mounted() {
            this.aliasesParams = {
                domain: this.domain,
            }
        },
        methods: {            
            edit(alias) {
                this.showEditModal = true;                
                this.editForm.set({
                    address: alias.address,
                    goto: alias.goto_array.map(to => {
                            return to;
                        }),
                });
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('postfix/aliases/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.showAddModal = true;
                this.addForm.set({
                    goto: ['']
                });
            },
            store() {
                this.addFormIsLoading = true;
                this.addForm.errors.clear();
                this.$store.dispatch('postfix/aliases/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(alias) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${alias.address}</strong>" alias.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/aliases/deactivate', alias.address);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(alias) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${alias.address}</strong>" alias.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/aliases/activate', alias.address);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was successfully activated.'
                        })
                    }
                })                
            },
            delete(alias) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${alias.address}</strong>" alias permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('postfix/aliases/delete', alias.address);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Alias was successfully deleted.'
                        })
                    }
                })                
            },
            addGoto(form) {
                if (form == 'edit') {
                    this.editForm.goto.push('');
                }

                if (form == 'add') {
                    this.addForm.goto.push('');
                }
            },
            deleteGoto(form, index) {
                if (form == 'edit') {
                    this.editForm.goto.splice(index, 1);
                }

                if (form == 'add') {
                    this.addForm.goto.splice(index, 1);
                }
            },       
        }
    }
</script>